/**
 * Géométrie des graphiques du rapport de diagnostic.
 *
 * Portage à l'identique des fonctions de `../mocks/report/js/script.js`. Le
 * document autonome a été relu et validé sur ces tracés : la moindre dérive
 * numérique (un arrondi déplacé, un signe d'angle inversé) redessine *tous* les
 * chemins du rapport et invalide la relecture. On ne « simplifie » rien ici.
 *
 * Aucune dépendance au DOM : ce module est du calcul pur, testable sans
 * navigateur, et c'est ce qui permet de comparer un `d=` produit ici avec celui
 * pré-rendu dans `index.html`.
 */

/** Un point du plan, en unités du `viewBox`. */
export interface ChartPoint {
  x: number;
  y: number;
}

/** Ancrage horizontal d'un texte SVG. */
export type TextAnchor = 'start' | 'middle' | 'end';

/**
 * Arrondi à trois décimales.
 *
 * Trois décimales, pas plus : c'est la précision retenue par le document
 * d'origine pour que les chaînes de chemin restent lisibles et diffables. Toute
 * valeur qui finit dans un attribut passe par ici, sinon le markup produit
 * cesse d'être comparable au pré-rendu.
 */
export function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/** Borne une valeur dans un intervalle, sans surprise si `min > max`. */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Coordonnées polaires — angle en degrés, **sens trigonométrique inversé**.
 *
 * Le radian vaut `-angle * π / 180` : 90° pointe vers le haut et les angles
 * *décroissants* tournent dans le sens horaire. Tous les secteurs du rapport
 * partent de 90° et descendent ; changer le signe retournerait chaque anneau.
 */
export function polar(cx: number, cy: number, radius: number, angle: number): ChartPoint {
  const rad = (-angle * Math.PI) / 180;
  return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
}

/**
 * Secteur d'anneau (couronne) entre deux rayons et deux angles.
 *
 * `largeArc` dès que l'écart dépasse 180°, `sweep` déduit du signe de l'écart :
 * comme les angles décroissent, `delta` est négatif et le balayage vaut 1 à
 * l'extérieur, donc `1 - sweep` au retour sur le rayon intérieur.
 */
export function sectorPath(
  cx: number,
  cy: number,
  innerR: number,
  outerR: number,
  startAngle: number,
  endAngle: number,
): string {
  const delta = endAngle - startAngle;
  const largeArc = Math.abs(delta) > 180 ? 1 : 0;
  const sweep = delta < 0 ? 1 : 0;
  const o0 = polar(cx, cy, outerR, startAngle);
  const o1 = polar(cx, cy, outerR, endAngle);
  const i0 = polar(cx, cy, innerR, endAngle);
  const i1 = polar(cx, cy, innerR, startAngle);

  return (
    'M' + round(o0.x) + ',' + round(o0.y) +
    'A' + round(outerR) + ',' + round(outerR) + ' 0 ' + largeArc + ' ' + sweep +
    ' ' + round(o1.x) + ',' + round(o1.y) +
    'L' + round(i0.x) + ',' + round(i0.y) +
    'A' + round(innerR) + ',' + round(innerR) + ' 0 ' + largeArc + ' ' + (1 - sweep) +
    ' ' + round(i1.x) + ',' + round(i1.y) +
    'Z'
  );
}

/**
 * Courbe cubique monotone (Fritsch–Carlson).
 *
 * Une spline de Catmull-Rom dépasserait entre deux paliers scolaires voisins et
 * ferait passer la courbe par un niveau que l'élève n'a pas : la monotonie n'est
 * pas cosmétique, elle empêche le graphique de mentir. Les deux garde-fous du
 * papier sont repris tels quels — mise à zéro de la tangente au changement de
 * signe, puis projection du couple de tangentes dans le cercle de rayon 3.
 */
export function monotonePath(points: readonly ChartPoint[]): string {
  const n = points.length;
  if (n === 0) return '';
  if (n === 1) return 'M' + round(points[0].x) + ',' + round(points[0].y);

  const dx: number[] = [];
  const slope: number[] = [];
  let i: number;
  for (i = 0; i < n - 1; i += 1) {
    dx.push(points[i + 1].x - points[i].x);
    slope.push(dx[i] === 0 ? 0 : (points[i + 1].y - points[i].y) / dx[i]);
  }

  // Extrémités : pente unilatérale. Intérieur : moyenne, annulée si les pentes
  // s'opposent (un extremum local doit rester plat, pas rebondir).
  const m: number[] = [slope[0]];
  for (i = 1; i < n - 1; i += 1) {
    m.push(slope[i - 1] * slope[i] <= 0 ? 0 : (slope[i - 1] + slope[i]) / 2);
  }
  m.push(slope[n - 2]);

  for (i = 0; i < n - 1; i += 1) {
    if (slope[i] === 0) {
      m[i] = 0;
      m[i + 1] = 0;
      continue;
    }
    const a = m[i] / slope[i];
    const b = m[i + 1] / slope[i];
    const s = a * a + b * b;
    if (s > 9) {
      const scale = 3 / Math.sqrt(s);
      m[i] = scale * a * slope[i];
      m[i + 1] = scale * b * slope[i];
    }
  }

  let d = 'M' + round(points[0].x) + ',' + round(points[0].y);
  for (i = 0; i < n - 1; i += 1) {
    const third = dx[i] / 3;
    d +=
      'C' + round(points[i].x + third) + ',' + round(points[i].y + m[i] * third) +
      ' ' + round(points[i + 1].x - third) + ',' + round(points[i + 1].y - m[i + 1] * third) +
      ' ' + round(points[i + 1].x) + ',' + round(points[i + 1].y);
  }
  return d;
}

/**
 * Barre à coins arrondis en haut seulement.
 *
 * Le rayon est plafonné par `w/2` **et** par `h` : sans ce plafond une barre
 * proche de zéro se retourne sur elle-même et dessine un crochet.
 */
export function topRoundedBar(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): string {
  const r = Math.min(radius, width / 2, height);
  return (
    'M' + round(x) + ',' + round(y + height) +
    'L' + round(x) + ',' + round(y + r) +
    'Q' + round(x) + ',' + round(y) + ' ' + round(x + r) + ',' + round(y) +
    'L' + round(x + width - r) + ',' + round(y) +
    'Q' + round(x + width) + ',' + round(y) + ' ' + round(x + width) + ',' + round(y + r) +
    'L' + round(x + width) + ',' + round(y + height) +
    'Z'
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Cadres et axes — modèles de vue partagés par les graphiques cartésiens
   ───────────────────────────────────────────────────────────────────────── */

/** Marges du tracé, dans l'ordre CSS. */
export interface PlotMargin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** Zone de tracé utile, une fois marges et axes retranchés. */
export interface PlotBox {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
}

/**
 * Découpe la surface en zone de tracé.
 *
 * L'axe des ordonnées bascule d'un bord à l'autre selon la direction de
 * lecture : en RTL il passe à droite et le tracé commence au ras de la marge
 * gauche. C'est la seule chose que le miroir change côté cadre.
 */
export function plotBox(
  width: number,
  height: number,
  margin: PlotMargin,
  axisWidth: number,
  xAxisHeight: number,
  rtl: boolean,
): PlotBox {
  const left = margin.left + (rtl ? 0 : axisWidth);
  const right = width - margin.right - (rtl ? axisWidth : 0);
  const top = margin.top;
  const bottom = height - margin.bottom - xAxisHeight;
  return { left, right, top, bottom, width: right - left, height: bottom - top };
}

/** Ligne de grille horizontale. */
export interface GridLine {
  key: string | number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** Graduation textuelle d'un axe. */
export interface AxisTick {
  key: string | number;
  x: number;
  y: number;
  label: string;
  anchor: TextAnchor;
}

/** Abscisse d'une graduation d'ordonnée : 8 px en dehors du tracé. */
export function yTickX(box: PlotBox, rtl: boolean): number {
  return round(rtl ? box.right + 8 : box.left - 8);
}

/** Centre de la n-ième bande d'une échelle ordinale. */
export function bandCentre(box: PlotBox, band: number, index: number): number {
  return box.left + band * (index + 0.5);
}

/**
 * Ancrage d'une étiquette rayonnante autour d'un centre.
 *
 * La zone morte de ±4 px évite qu'une étiquette quasi verticale saute de
 * `start` à `end` au gré des arrondis.
 */
export function radialAnchor(x: number, cx: number): TextAnchor {
  if (x > cx + 4) return 'start';
  if (x < cx - 4) return 'end';
  return 'middle';
}

/**
 * Code de compétence tel que le lecteur le voit.
 *
 * Affichage uniquement : `data-code` conserve partout le code brut, qui reste
 * la clé de jointure entre le tableau, la légende, les couleurs et l'export.
 */
export function displayCode(code: string, prefix: string): string {
  if (!prefix || prefix === 'C') return code;
  return String(code).replace(/^C/, prefix);
}
